<template>
  <div>
    <b-card
      v-if="load"
      class="text-center p-5"
    >
      <b-spinner variant="primary" />
      <p class="mt-2">
        جاري التحميل...
      </p>
    </b-card>

    <b-card v-else-if="fixedPoint">
      <template #header>
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
          <h4 class="mb-2 mb-md-0 mobile-title">
            تفاصيل النقطة الثابتة: {{ fixedPoint.code }}
          </h4>
          <div class="d-flex flex-wrap gap-2 w-100 w-md-auto">
            <b-button
              variant="primary"
              class="flex-fill flex-md-none"
              @click="editFixedPoint"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-1"
              />
              تعديل
            </b-button>
            <b-button
              variant="secondary"
              class="flex-fill flex-md-none"
              @click="$router.go(-1)"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="14"
                class="mr-1"
              />
              العودة
            </b-button>
          </div>
        </div>
      </template>

      <b-row>
        <!-- Basic Information -->
        <b-col cols="12">
          <h5 class=" section-title">
            المعلومات الأساسية
          </h5>
          <b-row>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>الكود:</strong>
              <p class="mb-0">
                {{ fixedPoint.code }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>المستوى:</strong>
              <p class="mb-0">
                {{ fixedPoint.level || '-' }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>درجة الدقة:</strong>
              <p class="mb-0">
                <b-badge
                  :style="{
                    backgroundColor: fixedPoint.accuracy_degree_color,
                    color: 'white',
                  }"
                  class="px-2 py-1"
                >
                  {{ fixedPoint.accuracy_degree_label_ar }}
                </b-badge>
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>الموظف المسؤول:</strong>
              <p class="mb-0">
                {{
                  fixedPoint.responsible_employee
                    ? fixedPoint.responsible_employee.full_name
                    : '-'
                }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="12"
              md="6"
              class=" mb-md-0"
            >
              <strong>فريق المتابعة:</strong>
              <div v-if="Array.isArray(fixedPoint.monitoring_teams) && fixedPoint.monitoring_teams.length">
                <div
                  v-for="member in fixedPoint.monitoring_teams"
                  :key="member.id"
                  class="mb-50"
                >
                  <span>{{ member.full_name || member.name || '-' }}</span>
                  <small
                    v-if="member.phone || member.email"
                    class="text-muted d-block"
                  >
                    {{ [member.phone, member.email].filter(Boolean).join(' - ') }}
                  </small>
                </div>
              </div>
              <p
                v-else
                class="mb-0 text-muted"
              >
                No monitoring team assigned
              </p>
            </b-col>
            <b-col
              v-if="fixedPoint.description"
              cols="12"
              sm="12"
              md="6"
              class=" mb-md-0"
            >
              <strong>الوصف:</strong>
              <p class="mb-0">
                {{ fixedPoint.description }}
              </p>
            </b-col>
            <b-col
              v-if="fixedPoint.coord_type"
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>نوع الإحداثيات:</strong>
              <p class="mb-0">
                <b-badge
                  variant="info"
                  class="px-2 py-1"
                >
                  {{ getCoordTypeLabel(fixedPoint.coord_type) }}
                </b-badge>
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>نوع التصحيح:</strong>
              <p class="mb-0">
                <b-badge
                  variant="primary"
                  class="px-2 py-1"
                >
                  {{ getCorrectionTypeLabel(fixedPoint.correction_type) }}
                </b-badge>
              </p>
            </b-col>

          </b-row>
        </b-col>

        <!-- Descriptive Location -->
        <b-col cols="12">
          <h5 class=" section-title">
            الموقع الوصفي
          </h5>
          <b-row>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>المحافظة:</strong>
              <p class="mb-0">
                {{ fixedPoint.governorate && fixedPoint.governorate.name_ar || '-' }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>الجهاز / الحي / المركز:</strong>
              <p class="mb-0">
                {{ fixedPoint.center && fixedPoint.center.name_ar || '-' }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>الوحدة المحلية / المدينة:</strong>
              <p class="mb-0">
                {{ fixedPoint.city && fixedPoint.city.name_ar || '-' }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>القرية / التجمع:</strong>
              <p class="mb-0">
                {{ fixedPoint.district && fixedPoint.district.name_ar || '-' }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>عزبة / نجع / منطقة:</strong>
              <p class="mb-0">
                {{ fixedPoint.village && fixedPoint.village.name_ar || '-' }}
              </p>
            </b-col>
          </b-row>
        </b-col>

        <!-- Geographic Coordinates -->
        <b-col
          v-if="fixedPoint.coord_type === 'lat_lon' && fixedPoint.latitude && fixedPoint.longitude"
          cols="12"
        >
          <h5 class=" section-title">
            الإحداثيات الجغرافية (Latitude/Longitude)
          </h5>
          <b-row>
            <b-col
              cols="12"
              md="6"
              class=" mb-md-0"
            >
              <strong>خط العرض (Latitude):</strong>
              <p class="mb-0">
                {{ fixedPoint.latitude }}
              </p>
            </b-col>
            <b-col
              cols="12"
              md="6"
              class=" mb-md-0"
            >
              <strong>خط الطول (Longitude):</strong>
              <p class="mb-0">
                {{ fixedPoint.longitude }}
              </p>
            </b-col>
          </b-row>
          <b-button
            variant="outline-primary"
            size="sm"
            class="mt-2 w-100 w-md-auto"
            @click="openInGoogleMaps"
          >
            <feather-icon
              icon="MapIcon"
              size="14"
              class="mr-1"
            />
            فتح في خرائط جوجل
          </b-button>
        </b-col>

        <!-- Corrected Geographic Coordinates -->
        <b-col
          v-if="fixedPoint.corrected_latitude && fixedPoint.corrected_longitude"
          cols="12"
        >
          <h5
            class=" section-title"
            style="color: #ff9800;"
          >
            الإحداثيات الجغرافية المصححة (Corrected Latitude/Longitude)
          </h5>
          <b-row>
            <b-col
              cols="12"
              md="6"
              class=" mb-md-0"
            >
              <strong>خط العرض المصحح (Corrected Latitude):</strong>
              <p class="mb-0">
                {{ fixedPoint.corrected_latitude }}
              </p>
            </b-col>
            <b-col
              cols="12"
              md="6"
              class=" mb-md-0"
            >
              <strong>خط الطول المصحح (Corrected Longitude):</strong>
              <p class="mb-0">
                {{ fixedPoint.corrected_longitude }}
              </p>
            </b-col>
          </b-row>
          <b-button
            variant="outline-warning"
            size="sm"
            class="mt-2 w-100 w-md-auto"
            @click="openCorrectedInGoogleMaps"
          >
            <feather-icon
              icon="MapIcon"
              size="14"
              class="mr-1"
            />
            فتح الإحداثيات المصححة في خرائط جوجل
          </b-button>
        </b-col>

        <!-- ETM Coordinates -->
        <b-col
          v-if="fixedPoint.coord_type === 'etm' && fixedPoint.etm_est && fixedPoint.etm_north"

          cols="12"
        >
          <h5 class=" section-title">
            إحداثيات ETM
          </h5>
          <b-row>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>ETM East:</strong>
              <p class="mb-0">
                {{ fixedPoint.etm_est }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>ETM North:</strong>
              <p class="mb-0">
                {{ fixedPoint.etm_north }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>ETM Zone:</strong>
              <p class="mb-0">
                {{ fixedPoint.etm_zone || '-' }}
              </p>
            </b-col>
          </b-row>
        </b-col>

        <!-- Corrected ETM Coordinates -->
        <b-col
          v-if="fixedPoint.corrected_etm_est && fixedPoint.corrected_etm_north"
          cols="12"
        >
          <h5
            class=" section-title"
            style="color: #ff9800;"
          >
            إحداثيات ETM المصححة (Corrected ETM)
          </h5>
          <b-row>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>ETM East المصحح:</strong>
              <p class="mb-0">
                {{ fixedPoint.corrected_etm_est }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>ETM North المصحح:</strong>
              <p class="mb-0">
                {{ fixedPoint.corrected_etm_north }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>ETM Zone المصحح:</strong>
              <p class="mb-0">
                {{ fixedPoint.corrected_etm_zone || '-' }}
              </p>
            </b-col>
          </b-row>
        </b-col>

        <!-- UTM Coordinates -->
        <b-col
          v-if="fixedPoint.coord_type === 'utm' && fixedPoint.utm_est && fixedPoint.utm_north"
          cols="12"
          class="mb-4"
        >
          <h5 class=" section-title">
            إحداثيات UTM
          </h5>
          <b-row>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>UTM East:</strong>
              <p class="mb-0">
                {{ Number(fixedPoint.utm_est).toFixed(3) }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>UTM North:</strong>
              <p class="mb-0">
                {{ Number(fixedPoint.utm_north).toFixed(3) }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>المنسوب:</strong>
              <p class="mb-0">
                {{ fixedPoint.index }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>UTM Zone:</strong>
              <p class="mb-0">
                {{ fixedPoint.utm_zone || '-' }}
              </p>
            </b-col>
          </b-row>
        </b-col>

        <!-- Corrected UTM Coordinates -->
        <b-col
          v-if="fixedPoint.corrected_utm_est && fixedPoint.corrected_utm_north"
          cols="12"
          class="mb-4"
        >
          <h5
            class=" section-title"
            style="color: #ff9800;"
          >
            إحداثيات UTM المصححة (Corrected UTM)
          </h5>
          <b-row>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>UTM East المصحح:</strong>
              <p class="mb-0">
                {{ Number(fixedPoint.corrected_utm_est).toFixed(3) }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>UTM North المصحح:</strong>
              <p class="mb-0">
                {{ Number(fixedPoint.corrected_utm_north).toFixed(3) }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="3"
              class=" mb-md-0"
            >
              <strong>UTM Zone المصحح:</strong>
              <p class="mb-0">
                {{ fixedPoint.corrected_utm_zone || '-' }}
              </p>
            </b-col>
          </b-row>
        </b-col>

        <!-- Corrected Specific Media Files -->
        <b-col
          v-if="hasCorrectedSpecificMedia"
          cols="12"
          class="mb-4"
        >
          <h5
            class=" section-title"
            style="color: #ff9800;"
          >
            ملفات الوسائط المصححة المرتبطة
          </h5>
          <b-row>
            <!-- Corrected RINEX Media -->
            <b-col
              v-if="fixedPoint.corrected_rinex_media"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف RINEX المصحح
                </h6>
                <b-avatar
                  :src="fixedPoint.corrected_rinex_media.url"
                  size="80px"
                  rounded
                  class="mb-2"
                />
                <p class="mb-1">
                  {{ fixedPoint.corrected_rinex_media.original_name || fixedPoint.corrected_rinex_media.name }}
                </p>
                <small class="text-muted">{{ fixedPoint.corrected_rinex_media.human_readable_size || formatFileSize(fixedPoint.corrected_rinex_media.size) }}</small>
                <div class="mt-2 d-flex justify-content-center">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :href="fixedPoint.corrected_rinex_media.download_url || fixedPoint.corrected_rinex_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      size="12"
                    />
                    تحميل
                  </b-button>
                  <b-button
                    variant="outline-info"
                    size="sm"
                    :href="fixedPoint.corrected_rinex_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="12"
                    />
                    عرض
                  </b-button>
                </div>
              </b-card>
            </b-col>
            <b-col
              v-else-if="fixedPoint.corrected_rinex_media_id"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف RINEX المصحح
                </h6>
                <p class="text-muted">
                  الملف غير متاح حالياً
                </p>
                <small class="text-muted">ID: {{ fixedPoint.corrected_rinex_media_id }}</small>
              </b-card>
            </b-col>

            <!-- Corrected Raw Data Media -->
            <b-col
              v-if="fixedPoint.corrected_raw_data_media"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف البيانات الخام المصحح
                </h6>
                <b-avatar
                  :src="fixedPoint.corrected_raw_data_media.url"
                  size="80px"
                  rounded
                  class="mb-2"
                />
                <p class="mb-1">
                  {{ fixedPoint.corrected_raw_data_media.original_name || fixedPoint.corrected_raw_data_media.name }}
                </p>
                <small class="text-muted">{{ fixedPoint.corrected_raw_data_media.human_readable_size || formatFileSize(fixedPoint.corrected_raw_data_media.size) }}</small>
                <div class="mt-2 d-flex justify-content-center">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :href="fixedPoint.corrected_raw_data_media.download_url || fixedPoint.corrected_raw_data_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      size="12"
                    />
                    تحميل
                  </b-button>
                  <b-button
                    variant="outline-info"
                    size="sm"
                    :href="fixedPoint.corrected_raw_data_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="12"
                    />
                    عرض
                  </b-button>
                </div>
              </b-card>
            </b-col>
            <b-col
              v-else-if="fixedPoint.corrected_raw_data_media_id"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف البيانات الخام المصحح
                </h6>
                <p class="text-muted">
                  الملف غير متاح حالياً
                </p>
                <small class="text-muted">ID: {{ fixedPoint.corrected_raw_data_media_id }}</small>
              </b-card>
            </b-col>

            <!-- Corrected DWG Media -->
            <b-col
              v-if="fixedPoint.corrected_dwg_media"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف DWG المصحح (AutoCAD)
                </h6>
                <b-avatar
                  :src="fixedPoint.corrected_dwg_media.url"
                  size="80px"
                  rounded
                  class="mb-2"
                />
                <p class="mb-1">
                  {{ fixedPoint.corrected_dwg_media.original_name || fixedPoint.corrected_dwg_media.name }}
                </p>
                <small class="text-muted">{{ fixedPoint.corrected_dwg_media.human_readable_size || formatFileSize(fixedPoint.corrected_dwg_media.size) }}</small>
                <div class="mt-2 d-flex justify-content-center">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :href="fixedPoint.corrected_dwg_media.download_url || fixedPoint.corrected_dwg_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      size="12"
                    />
                    تحميل
                  </b-button>
                  <b-button
                    variant="outline-info"
                    size="sm"
                    :href="fixedPoint.corrected_dwg_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="12"
                    />
                    عرض
                  </b-button>
                </div>
              </b-card>
            </b-col>
            <b-col
              v-else-if="fixedPoint.corrected_DWG_media_id"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف DWG المصحح (AutoCAD)
                </h6>
                <p class="text-muted">
                  الملف غير متاح حالياً
                </p>
                <small class="text-muted">ID: {{ fixedPoint.corrected_DWG_media_id }}</small>
              </b-card>
            </b-col>
          </b-row>
        </b-col>

        <!-- Corrected Bulk Media Files -->
        <b-col
          v-if="fixedPoint.corrected_media && fixedPoint.corrected_media.length > 0"
          cols="12"
          class="mb-4"
        >
          <h5
            class=" section-title"
            style="color: #ff9800;"
          >
            ملفات الوسائط المصححة المجمعة ({{ fixedPoint.corrected_media.length }} ملف)
          </h5>
          <b-row>
            <b-col
              v-for="mediaItem in fixedPoint.corrected_media"
              :key="mediaItem.id"
              cols="12"
              sm="6"
              md="4"
              lg="3"
              class="mb-3"
            >
              <b-card class="text-center">
                <b-avatar
                  :src="mediaItem.url"
                  size="100px"
                  rounded
                  class="mb-2"
                />
                <p class="mb-1 font-weight-bold">
                  {{ mediaItem.name || mediaItem.file_name }}
                </p>
                <small class="text-muted d-block">{{ formatFileSize(mediaItem.size) }}</small>
                <small
                  v-if="mediaItem.pivot && mediaItem.pivot.type"
                  class="text-muted d-block"
                >
                  النوع: {{ getMediaTypeLabel(mediaItem.pivot.type) }}
                </small>
                <p
                  v-if="mediaItem.pivot && mediaItem.pivot.notes"
                  class="mb-1 small text-muted"
                >
                  {{ mediaItem.pivot.notes }}
                </p>
                <small
                  v-if="mediaItem.pivot && mediaItem.pivot.created_at"
                  class="text-muted d-block"
                >
                  تاريخ الرفع: {{ formatDate(mediaItem.pivot.created_at) }}
                </small>
                <div class="mt-2 d-flex justify-content-center">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :href="mediaItem.download_url || mediaItem.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      size="12"
                    />
                    تحميل
                  </b-button>
                  <b-button
                    variant="outline-info"
                    size="sm"
                    :href="mediaItem.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="12"
                    />
                    عرض
                  </b-button>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </b-col>

        <!-- Associated Media Files (RINEX, Raw Data, DWG) -->
        <b-col
          v-if="hasAssociatedMedia"
          cols="12"
          class="mb-4"
        >
          <h5 class=" section-title">
            ملفات الوسائط المرتبطة
          </h5>
          <b-row>
            <!-- RINEX Media -->
            <b-col
              v-if="fixedPoint.rinex_media"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف RINEX
                </h6>
                <b-avatar
                  :src="fixedPoint.rinex_media.url"
                  size="80px"
                  rounded
                  class="mb-2"
                />
                <p class="mb-1">
                  {{ fixedPoint.rinex_media.original_name || fixedPoint.rinex_media.name }}
                </p>
                <small class="text-muted">{{ fixedPoint.rinex_media.human_readable_size || formatFileSize(fixedPoint.rinex_media.size) }}</small>
                <div class="mt-2 d-flex justify-content-center">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :href="fixedPoint.rinex_media.download_url || fixedPoint.rinex_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      size="12"
                    />
                    تحميل
                  </b-button>
                  <b-button
                    variant="outline-info"
                    size="sm"
                    :href="fixedPoint.rinex_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="12"
                    />
                    عرض
                  </b-button>
                </div>
              </b-card>
            </b-col>
            <b-col
              v-else-if="fixedPoint.rinex_media_id"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف RINEX
                </h6>
                <p class="text-muted">
                  الملف غير متاح حالياً
                </p>
                <small class="text-muted">ID: {{ fixedPoint.rinex_media_id }}</small>
              </b-card>
            </b-col>

            <!-- Raw Data Media -->
            <b-col
              v-if="fixedPoint.raw_data_media"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف البيانات الخام
                </h6>
                <b-avatar
                  :src="fixedPoint.raw_data_media.url"
                  size="80px"
                  rounded
                  class="mb-2"
                />
                <p class="mb-1">
                  {{ fixedPoint.raw_data_media.original_name || fixedPoint.raw_data_media.name }}
                </p>
                <small class="text-muted">{{ fixedPoint.raw_data_media.human_readable_size || formatFileSize(fixedPoint.raw_data_media.size) }}</small>
                <div class="mt-2 d-flex justify-content-center">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :href="fixedPoint.raw_data_media.download_url || fixedPoint.raw_data_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      size="12"
                    />
                    تحميل
                  </b-button>
                  <b-button
                    variant="outline-info"
                    size="sm"
                    :href="fixedPoint.raw_data_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="12"
                    />
                    عرض
                  </b-button>
                </div>
              </b-card>
            </b-col>
            <b-col
              v-else-if="fixedPoint.raw_data_media_id"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف البيانات الخام
                </h6>
                <p class="text-muted">
                  الملف غير متاح حالياً
                </p>
                <small class="text-muted">ID: {{ fixedPoint.raw_data_media_id }}</small>
              </b-card>
            </b-col>

            <!-- DWG Media -->
            <b-col
              v-if="fixedPoint.dwg_media"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف DWG (AutoCAD)
                </h6>
                <b-avatar
                  :src="fixedPoint.dwg_media.url"
                  size="80px"
                  rounded
                  class="mb-2"
                />
                <p class="mb-1">
                  {{ fixedPoint.dwg_media.original_name || fixedPoint.dwg_media.name }}
                </p>
                <small class="text-muted">{{ fixedPoint.dwg_media.human_readable_size || formatFileSize(fixedPoint.dwg_media.size) }}</small>
                <div class="mt-2 d-flex justify-content-center">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :href="fixedPoint.dwg_media.download_url || fixedPoint.dwg_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      size="12"
                    />
                    تحميل
                  </b-button>
                  <b-button
                    variant="outline-info"
                    size="sm"
                    :href="fixedPoint.dwg_media.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="12"
                    />
                    عرض
                  </b-button>
                </div>
              </b-card>
            </b-col>
            <b-col
              v-else-if="fixedPoint.DWG_media_id"
              cols="12"
              sm="6"
              md="4"
              class=""
            >
              <b-card class="text-center">
                <h6 class="mb-2">
                  ملف DWG (AutoCAD)
                </h6>
                <p class="text-muted">
                  الملف غير متاح حالياً
                </p>
                <small class="text-muted">ID: {{ fixedPoint.DWG_media_id }}</small>
              </b-card>
            </b-col>
          </b-row>
        </b-col>

        <!-- General Media Files -->
        <b-col
          v-if="fixedPoint.media && fixedPoint.media.length > 0"
          cols="12"
        >
          <h5 class=" section-title">
            الملفات المرفقة
          </h5>
          <b-row>
            <b-col
              v-for="mediaItem in fixedPoint.media"
              :key="mediaItem.id"
              cols="12"
              sm="6"
              md="4"
              lg="3"
              class=""
            >
              <b-card class="text-center">
                <b-avatar
                  :src="mediaItem.url"
                  size="100px"
                  rounded
                  class="mb-2"
                />
                <p class="mb-1">
                  {{ mediaItem.name }}
                </p>
                <small class="text-muted">{{ formatFileSize(mediaItem.size) }}</small>
                <div class="mt-2 d-flex justify-content-center">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    :href="mediaItem.download_url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      size="12"
                    />
                  </b-button>
                  <b-button
                    variant="outline-info"
                    size="sm"
                    :href="mediaItem.url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="12"
                    />
                  </b-button>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="deleteMedia(mediaItem.id)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="12"
                    />
                  </b-button>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </b-col>

        <!-- Metadata -->
        <b-col cols="12">
          <h5 class=" section-title">
            معلومات إضافية
          </h5>
          <b-row>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>تاريخ الإنشاء:</strong>
              <p class="mb-0">
                {{ formatDate(fixedPoint.created_at) }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>تاريخ آخر تحديث:</strong>
              <p class="mb-0">
                {{ formatDate(fixedPoint.updated_at) }}
              </p>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              md="4"
              class=" mb-md-0"
            >
              <strong>أنشأ بواسطة:</strong>
              <p class="mb-0">
                {{ fixedPoint.creator ? fixedPoint.creator.name : '-' }}
              </p>
            </b-col>
          </b-row>
        </b-col>

        <!-- Activity Logs -->
        <b-col
          v-if="fixedPoint && fixedPoint.id && hasPermission('view activity_logs')"
          cols="12"
        >
          <h5 class=" section-title">
            سجل النشاطات
          </h5>
          <ActivityLogs
            :model-type="'fixed-point'"
            :model-id="fixedPoint.id"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-card v-else>
      <div class="text-center p-5">
        <p class="text-muted">
          لم يتم العثور على النقطة الثابتة
        </p>
        <b-button
          variant="primary"
          @click="$router.go(-1)"
        >
          العودة
        </b-button>
      </div>
    </b-card>

    <!-- Edit Modal -->
    <FixedPointForm
      :show="showEditModal"
      :fixed-point="fixedPoint"
      @close="closeEditModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BBadge,
  BAvatar,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import FixedPointForm from './FixedPointForm.vue'
import ActivityLogs from './ActivityLogs.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BBadge,
    BAvatar,
    FeatherIcon,
    FixedPointForm,
    ActivityLogs,
  },
  computed: {
    ...mapGetters({
      fixedPoint: 'fixedPoints/fixedPoint',
      load: 'fixedPoints/load',
    }),
    hasAssociatedMedia() {
      if (!this.fixedPoint) return false
      return !!(
        this.fixedPoint.rinex_media
        || this.fixedPoint.raw_data_media
        || this.fixedPoint.dwg_media
        || this.fixedPoint.rinex_media_id
        || this.fixedPoint.raw_data_media_id
        || this.fixedPoint.DWG_media_id
      )
    },
    hasCorrectedSpecificMedia() {
      if (!this.fixedPoint) return false
      return !!(
        this.fixedPoint.corrected_rinex_media
        || this.fixedPoint.corrected_raw_data_media
        || this.fixedPoint.corrected_dwg_media
        || this.fixedPoint.corrected_rinex_media_id
        || this.fixedPoint.corrected_raw_data_media_id
        || this.fixedPoint.corrected_DWG_media_id
      )
    },
  },
  data() {
    return {
      showEditModal: false,
    }
  },
  mounted() {
    this.loadFixedPoint()
  },
  methods: {
    loadFixedPoint() {
      const { id } = this.$route.params
      this.$store.dispatch('fixedPoints/showFixedPoint', { id })
    },
    deleteMedia(id) {
      this.$swal({
        title: 'هل انت متأكد من حذف هذا الملف؟',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'نعم، حذف',
        cancelButtonText: 'إلغاء',
      }).then(result => {
        if (result.isConfirmed) {
          this.$store
            .dispatch('media/delete', { id })
            .then(() => {
              this.$toast.success('تم حذف الملف بنجاح')
              this.loadFixedPoint()
            })
            .catch(() => {
              this.$toast.error('حدث خطأ أثناء حذف الملف')
            })
        }
      })
    },
    editFixedPoint() {
      this.showEditModal = true
    },
    closeEditModal() {
      this.showEditModal = false
    },
    handleSaved() {
      this.closeEditModal()
      this.loadFixedPoint()
      this.$toast.success('تم التحديث بنجاح')
    },
    openInGoogleMaps() {
      if (this.fixedPoint.latitude && this.fixedPoint.longitude) {
        const url = `https://www.google.com/maps?q=${this.fixedPoint.latitude},${this.fixedPoint.longitude}`
        window.open(url, '_blank')
      }
    },
    openCorrectedInGoogleMaps() {
      if (this.fixedPoint.corrected_latitude && this.fixedPoint.corrected_longitude) {
        const url = `https://www.google.com/maps?q=${this.fixedPoint.corrected_latitude},${this.fixedPoint.corrected_longitude}`
        window.open(url, '_blank')
      }
    },
    getMediaTypeLabel(type) {
      const labels = {
        rinex: 'RINEX',
        raw_data: 'البيانات الخام',
        dwg: 'DWG',
        other: 'أخرى',
      }
      return labels[type] || type
    },
    getCoordTypeLabel(coordType) {
      const labels = {
        lat_lon: 'خطوط الطول والعرض',
        etm: 'ETM',
        utm: 'UTM',
      }
      return labels[coordType] || coordType
    },
    getCorrectionTypeLabel(correctionType) {
      return correctionType === 'after' ? 'بعد التصحيح' : 'قبل التصحيح'
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`
    },
  },
}
</script>

<style scoped>
strong {
  color: #5e5873;
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}
p {
  margin-bottom: 1rem;
  word-break: break-word;
}
.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #379e76;
  border-bottom: 2px solid #379e76;
  padding-bottom: 0.5rem;
}
.mobile-title {
  font-size: 1rem;
  word-break: break-word;
}
.gap-2 {
  gap: 0.5rem;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .mobile-title {
    font-size: 0.9rem;
  }
  .section-title {
    font-size: 1rem;
  }
  strong {
    font-size: 0.85rem;
  }
  p {
    font-size: 0.9rem;
  }
  h5 {
    font-size: 1rem;
  }
  h6 {
    font-size: 0.9rem;
  }
  .b-card {
    margin-bottom: 1rem;
  }
}

@media (max-width: 576px) {
  .mobile-title {
    font-size: 0.85rem;
  }
  .section-title {
    font-size: 0.95rem;
  }
  strong {
    font-size: 0.8rem;
  }
  p {
    font-size: 0.85rem;
  }
}
</style>
